import { useCallback, useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'

import { Album_AlbumAction } from '@jotta/grpc-connect-openapi/album'
import { CollectionType } from '@jotta/types/schemas/AlbumSchema'
import { AppLayoutPortal } from '@jotta/ui/AppLayoutPortal'
import { BackButton } from '@jotta/ui/BackButton'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { Breakpoint, useBreakpoint } from '@jotta/ui/useBreakpoint'

import { useAlbum } from '../../../api/useAlbum'
import type { CollectionRef } from '../../../api/useCollection'
import type { AlbumInfoStore } from '../../../store/AlbumInfoStore'
import { useSelectionStore } from '../../../store/PhotoContext'
import { MediaCarouselContainer } from '../../MediaCarousel/MediaCarouselContainer'
import { SelectionManager } from '../../SelectionManager/SelectionManager'
import { UploadPhotos } from '../../UploadPhotos/UploadPhotos'
import { AlbumActionPanel } from '../AlbumInfo/AlbumActionPanel'
import { AlbumInfo } from '../AlbumInfo/AlbumInfo'
import { AlbumPhotos, type AlbumPhotosHandle } from '../AlbumPhotos/AlbumPhotos'

const AlbumToolbar = observer<{
  collectionRef: CollectionRef
  albumStore: AlbumInfoStore
  backLocation?: string
}>(function AlbumToolbar({ collectionRef, albumStore, backLocation }) {
  const showToplevelToolbar = useBreakpoint(Breakpoint.xl)
  const { hasSelection } = useSelectionStore()

  const { data: album } = useAlbum(albumStore.albumIdOrShareId)

  const showUploadPhotosButton =
    Boolean(album?.actions.includes(Album_AlbumAction.ADD_PHOTOS)) &&
    !hasSelection

  if (showToplevelToolbar) {
    return (
      <AppLayoutPortal slot="moduleNav">
        <div className="flex w-full gap-2 rounded-lg">
          {backLocation && <BackButton location={backLocation} />}
          <div className="grow" />
          {showUploadPhotosButton && <UploadPhotos btnType="TONAL" />}
          <AlbumActionPanel collectionRef={collectionRef} store={albumStore} />
        </div>
      </AppLayoutPortal>
    )
  }

  return (
    <>
      {showUploadPhotosButton && (
        <AppLayoutPortal slot="actionButton">
          <UploadPhotos btnType="FAB" />
        </AppLayoutPortal>
      )}
      <AlbumActionPanel collectionRef={collectionRef} store={albumStore} />
    </>
  )
})

export const AlbumContainer = observer<{
  album: AlbumInfoStore
  mediaId?: string
  backLocation?: string
}>(function AlbumContainer({ album, mediaId, backLocation }) {
  const showLargePhotos = useBreakpoint(Breakpoint.xl)
  const albumPhotosRef = useRef<AlbumPhotosHandle>(null)
  const lastViewedIdRef = useRef<string | null>(mediaId ?? null)
  const previousMediaIdRef = useRef(mediaId)

  const handleCurrentMediaChange = useCallback((id: string) => {
    lastViewedIdRef.current = id
  }, [])

  useEffect(() => {
    const previousMediaId = previousMediaIdRef.current
    previousMediaIdRef.current = mediaId

    if (mediaId || !previousMediaId) {
      return
    }

    const focusId = lastViewedIdRef.current ?? previousMediaId
    if (!focusId) {
      return
    }

    albumPhotosRef.current?.focusPhotoById(focusId)
    lastViewedIdRef.current = null
  }, [mediaId])

  const showPhotos =
    album.albumType !== CollectionType.SHARED_PHOTOS ||
    album.photoIds.length !== 1

  const albumIdOrShareId = album.albumIdOrShareId

  const collectionRef = useAlbum(albumIdOrShareId).data?.collectionRef

  if (!collectionRef || !albumIdOrShareId) {
    return <LoadingOverlay id="AlbumContainer" position="absolute" open />
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <AlbumInfo
        albumIdOrShareId={albumIdOrShareId}
        collectionRef={collectionRef}
        store={album}
        goBig={!showPhotos}
      />
      <AlbumToolbar
        collectionRef={collectionRef}
        albumStore={album}
        backLocation={backLocation}
      />
      <AlbumPhotos
        ref={albumPhotosRef}
        ids={album.photoIds}
        showPhotos={showPhotos}
        showOwnerAvatar={album.isShared}
        size={showLargePhotos ? 'large' : 'small'}
        collectionRef={collectionRef}
      />

      <SelectionManager
        collectionRef={collectionRef}
        selectables={album.photos}
        actionContext={album.viewContexts.selection}
      />

      <MediaCarouselContainer
        collectionRef={collectionRef}
        ids={album.photoIds}
        items={album.photos}
        mediaId={mediaId}
        showOwnerName={album.model.showOwnerName}
        onCurrentMediaChange={handleCurrentMediaChange}
      />
    </div>
  )
})
